import type { CreditLimitExceededDetails, ReasonCode } from "@ai-platform/contracts";
import { formatMoney } from "./format.ts";

/**
 * Who reads a reason: the customer's users about their own account, or staff working on a
 * customer in the staff area (TL 2026-10-14).
 */
export type Audience = "customer" | "staff";

/**
 * One text per reason code of the permission function (K-04): the portal shows it next to a
 * locked button before the click and as the message after a 403 or 409, so the user reads the
 * same explanation both times. Customer-facing German in the "ihr" form of the design
 * prototype; wording is for marketing to adjust here, in one place.
 */
export const reasonTexts: Record<ReasonCode, string> = {
  role_required: "Dafür fehlt euch die Berechtigung in eurer Rolle.",
  // drafts of 2026-10-20 (ADR-133), marketing has the last word
  self_service_off: "Bestellungen und Stornos laufen in der Betaphase über unser Team; wendet euch an den Support.",
  customer_suspended: "Euer Kundenkonto ist gesperrt.",
  customer_not_approved: "Euer Kundenkonto ist noch nicht freigegeben.",
  credit_limit_exceeded: "Die Summe der Monatslimits würde eure Kreditgrenze überschreiten.",
  entitlement_not_live: "Diese Berechtigung ist nicht mehr aktiv.",
  demo_limit_fixed: "Das Limit einer Demo ist fest vorgegeben.",
  entitlement_exists: "Ihr habt dieses Produkt bereits.",
  order_open: "Für dieses Produkt läuft bereits eine Bestellung.",
  demo_already_used: "Für dieses Produkt hattet ihr bereits eine Demo.",
  demo_not_offered: "Für dieses Produkt gibt es keine Demo.",
  demo_only: "Dieses Produkt gibt es nur als Demo.",
  inquiry_only: "Dieses Produkt gibt es nur auf Anfrage.",
  period_invoiced: "Dieser Monat ist bereits abgerechnet.",
  invoice_reference_required: "Ohne Rechnungsnummer lässt sich die Bestellung nicht abschließen.",
  provisioning_open: "Vor dem Ende der Einrichtung lässt sich die Bestellung nicht abschließen.",
  order_closed: "Die Bestellung ist bereits abgeschlossen.",
  // drafts of 2026-10-14 (TL), marketing has the last word
  order_in_progress: "Ein Mitarbeiter bearbeitet die Bestellung bereits; wendet euch an den Support.",
  order_invoiced: "Die Bestellung ist bereits verrechnet; wendet euch an den Support.",
  customer_already_approved: "Der Kunde ist bereits freigegeben.",
  customer_already_suspended: "Der Kunde ist bereits gesperrt.",
  customer_not_suspended: "Der Kunde ist nicht gesperrt.",
  instance_in_use: "Diese Instanz gehört bereits zu einem anderen Kunden.",
  task_done: "Die Aufgabe ist bereits erledigt.",
  key_missing: "Es gibt keinen aktiven Schlüssel.",
  no_ai_access: "Dieses Produkt nutzt keine KI über die Plattform; dafür gibt es kein Monatslimit.",
  report_frozen: "Der Bericht ist bereits eingefroren.",
  inquiry_closed: "Die Anfrage ist bereits erledigt.",
  // the texts of ADR-148 point 2, for the staff's deletion of a product
  product_in_use: "Hat Bestellungen, Berechtigungen oder Anfragen und bleibt deshalb.",
  product_published: "Ist in dataCycle noch veröffentlicht. Erst dort aus der Veröffentlichung nehmen.",
};

/**
 * The staff area's text where the customer's would speak to the wrong account: staff act on a
 * customer, so "euer Kundenkonto" becomes "der Kunde". Every other reason reads the same for
 * both; staff are addressed with "ihr" like everyone else. Drafts of 2026-10-14 (TL), marketing
 * has the last word.
 */
export const staffReasonTexts: Partial<Record<ReasonCode, string>> = {
  customer_suspended: "Der Kunde ist gesperrt.",
  customer_not_approved: "Der Kunde ist noch nicht freigegeben.",
  credit_limit_exceeded: "Die Summe der Monatslimits würde die Kreditgrenze des Kunden überschreiten.",
  entitlement_exists: "Der Kunde hat dieses Produkt bereits.",
};

/**
 * The sentence that names the amounts of `credit_limit_exceeded` (UC-10), per reader: the
 * customer reads about their own credit limit, staff about the customer's.
 */
export const creditLimitDetailTexts: Record<Audience, (creditLimit: string, otherLimits: string) => string> = {
  customer: (creditLimit, otherLimits) =>
    `Eure Kreditgrenze ist ${creditLimit}, die übrigen Monatslimits ergeben zusammen ${otherLimits}.`,
  staff: (creditLimit, otherLimits) =>
    `Die Kreditgrenze ist ${creditLimit}, die übrigen Monatslimits ergeben zusammen ${otherLimits}.`,
};

/**
 * The text for a reason, or a general one when the API gave none. `audience` is required, so no
 * caller speaks to the customer by accident: `customer` gives `reasonTexts`, `staff` the text of
 * `staffReasonTexts` where there is one and `reasonTexts` otherwise.
 */
export function reasonText(reason: ReasonCode | undefined, audience: Audience): string {
  if (!reason) return "Das ist gerade nicht möglich.";
  return (audience === "staff" ? staffReasonTexts[reason] : undefined) ?? reasonTexts[reason];
}

/**
 * The text of `credit_limit_exceeded` with what UC-10 asks the message to name: the credit limit
 * and the sum of the other monthly limits, both from the refusal's `details`. Both sentences
 * follow `audience`, the reason's text as in `reasonText` and the amounts as in
 * `creditLimitDetailTexts`.
 */
export function creditLimitExceededText(details: CreditLimitExceededDetails, audience: Audience): string {
  const amounts = creditLimitDetailTexts[audience](formatMoney(details.creditLimit), formatMoney(details.otherLimits));
  return `${reasonText("credit_limit_exceeded", audience)} ${amounts}`;
}
